import { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { getSharedImage } from "../../services/api/editor/share";
import logger from "../../utils/logger";
import { computeFittedCanvasSize } from "../../features/canvas/computeFittedCanvasSize";
import "./Editor.css";
import { useImageContext } from "../../contexts/ImageContext";
import { useImageProcessing } from "../../hooks/editor/useImageProcessing";
import { usePassportProcessing } from "../../hooks/editor/usePassportProcessing";
import { getPassportPresets } from "../../services/api/catalog/passport_presets";
import { PASSPORT_CATEGORY_ID, sortPassportPresets, findPassportPresetByDimensions } from "../../utils/passportPresetUtils";
import { isPassportFlowActive, readPassportFlowSession } from "../../features/editor/passportFlowSession";
import { ENTRY_SOURCE, isResizeEntryActive, isResizeEntrySource, normalizePresetForResizeEditor, readResizeEntryFromSession, readTrendingEntryFromSession, resolveEntrySource, shouldApplyEntryResizePreset, resolveOpenToolPrep, resolveToolPrepEntryMode, clearToolPrepSession, TOOL_PREP_KEYS, TOOL_PREP_ENTRY, isToolPrepKey, toolPrepAllowsEmptySource } from "../../utils/editorEntrySession";
import { formatAspectRatioLabel, formatPixelDimensions } from "../../utils/getTransform";
import EditorTextFormatToolbar from "../../features/tools/watermark/Editor/EditorTextFormatToolbar";
import EditorWatermarkLeftPanelOverlays from "../../features/tools/watermark/Editor/EditorWatermarkLeftPanelOverlays";
import { WatermarkToolbarOverlayProvider } from "../../features/tools/watermark/Popup/WatermarkToolbarOverlayContext";
import { ToolPrepCloseContext } from "../../features/tools/shared/Popup/ToolPrepPopup";
import { useImageSize } from "../../hooks/editor/useImageSize";
import { useStudioFeatures } from "../../hooks/editor/useStudioFeatures";
import { useCanvasToolSync } from "../../hooks/editor/useCanvasToolSync";
import { useEditorSessionRestore } from "../../hooks/editor/useEditorSessionRestore";
import { useEditorSessionCommit } from "../../hooks/editor/useEditorSessionCommit";
import { useEditorUndoSync } from "../../hooks/editor/useEditorUndoSync";
import { isLayerVisible, useEditorStore } from "../../state/editorStore";
import { ensureBackgroundRemoved } from "../../utils/ensureBackgroundRemoved";
import { resolveCreationImageUrl } from "../../utils/resolveCreationImageUrl";
import { classifyImageLabelWithRetry, isFallbackClassifyLabel } from "../../utils/classifyImageLabel";
import { formatClassifyLabel } from "../../utils/formatClassifyLabel";
import { applyObjectLayerLabel, getRememberedObjectLabel, registerLibraryUploadOnce, rememberObjectLabel } from "../../utils/registerLibraryUploadOnce";
import { alignImageLayer } from "../../features/editor/alignImageLayer";
import { getImageDimensions } from "../../utils/imageUtils";
import EditorHeader from "../../features/editor/EditorHeader";
import EditorLeftMenu from "../../features/editor/EditorLeftMenu";
import EditorLeftMenuResizeControls from "../../features/tools/layout/resize/Editor/EditorLeftMenuResizeControls";
import EditorLeftMenuOutpaintControls from "../../features/tools/ai/outpaint/Editor/EditorLeftMenuOutpaintControls";
import { EditorLeftMenuBackgroundControls, EditorLeftMenuAiShadowControls, EditorLeftMenuSubjectControls, EditorLeftMenuUpscaleControls, EditorLeftMenuPassportControls, EditorLeftMenuLayersControls, EditorLeftMenuUploadControls } from "../../features/editor/panels";
import LayoutSettings from "../../components/popups/layout/LayoutSettings";
import CanvasEditor from "../../features/canvas/CanvasEditor";
import RoundLoader from "../../components/ui/RoundLoader";
import { IS_APP_V1 } from "../../config/app/appVersion";
import { useEditorToolPrep } from "../../features/editor/toolPrep/useEditorToolPrep";
import { useEditorStageActions } from "../../features/editor/stage/useEditorStageActions";

const AiProcessingLoader = () => (
  <div className="editor-page__round-loader-wrap">
    <RoundLoader size="lg" label="Processing…" />
  </div>
);

const Editor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSignedIn, getToken, isLoaded: isAuthLoaded } = useAuth();
  const {
    inputPreview,
    selectedFile,
    editorFlow,
    entrySource,
    passportPreset,
    entryResizePreset,
    startPassportFlow,
    startResizeEntryFlow,
    setPassportPreset,
    clearImage,
  } = useImageContext();

  const shareIdParam = (searchParams.get("shareId") || "").trim();
  const isShareMode =
    Boolean(shareIdParam) || searchParams.get("isShared") === "true";
  const isSharedGuest = isShareMode && !isSignedIn;

  const [shareLoading, setShareLoading] = useState(Boolean(shareIdParam));
  const [shareError, setShareError] = useState(null);
  const [sharedByName, setSharedByName] = useState(
    () => (searchParams.get("sharedBy") || "").trim() || null
  );
  const canvasEditorRef = useRef(null);
  const workspaceRef = useRef(null);
  const background = useEditorStore((s) => s.background);
  const setBackground = useEditorStore((s) => s.setBackground);
  const resetBackground = useEditorStore((s) => s.resetBackground);
  const setLayerVisibility = useEditorStore((s) => s.setLayerVisibility);
  const bumpAiGeneration = useEditorStore((s) => s.bumpAiGeneration);
  const applyAiResult = useEditorStore((s) => s.applyAiResult);
  const applyManualEdit = useEditorStore((s) => s.applyManualEdit);
  const setShadow = useEditorStore((s) => s.setShadow);
  const setOutline = useEditorStore((s) => s.setOutline);
  const setWatermark = useEditorStore((s) => s.setWatermark);
  const updateObject = useEditorStore((s) => s.updateObject);
  const seedHistoryBaseline = useEditorStore((s) => s.seedHistoryBaseline);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const canUndo = useEditorStore((s) => s.past.length > 0);
  const canRedo = useEditorStore((s) => s.future.length > 0);
  const historySeeded = useEditorStore((s) => s._historySeeded);
  const storeCreationId = useEditorStore((s) => s.creationId);
  const layers = useEditorStore((s) => s.layers);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const updateImageLayer = useEditorStore((s) => s.updateImageLayer);
  const backgroundLayerVisible = isLayerVisible(layers, "background");
  const objectLayerVisible = isLayerVisible(layers, "object");
  const selectedImageLayer =
    selectedLayerId &&
    selectedLayerId !== "object" &&
    selectedLayerId !== "background"
      ? layers.find((l) => l.id === selectedLayerId && l.type === "image")
      : null;
  const [images, setImages] = useState([]);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [originalProcessedImage, setOriginalProcessedImage] = useState(null);
  const initialPassportFlow = isPassportFlowActive(location?.state);
  const initialResizeEntryFlow = isResizeEntryActive(location?.state);
  const savedResizeEntry = readResizeEntryFromSession();
  const savedTrendingEntry = readTrendingEntryFromSession();
  const locationEntryResizePreset = location?.state?.entryResizePreset || null;
  const resolvedEntrySource = resolveEntrySource(location?.state);
  const activeEntryResizePreset =
    locationEntryResizePreset ||
    entryResizePreset ||
    savedResizeEntry?.resizePreset ||
    savedTrendingEntry?.resizePreset ||
    null;
  const shouldApplyEntryPreset = shouldApplyEntryResizePreset(
    resolvedEntrySource,
    activeEntryResizePreset
  );
  const isResizeEntryFlow =
    isResizeEntrySource(resolvedEntrySource) && Boolean(activeEntryResizePreset);
  const initialOpenResizePanel =
    initialResizeEntryFlow || Boolean(location?.state?.openResizePanel && activeEntryResizePreset);

  const [activeMenuIndex, setActiveMenuIndex] = useState(() => {
    if (initialPassportFlow) return 6;
    if (initialOpenResizePanel) return 2;
    return 0;
  });
  const [backgroundControlPanelVisible, setBackgroundControlPanelVisible] = useState(false);
  const [resizeControlPanelVisible, setResizeControlPanelVisible] = useState(
    () => Boolean(initialOpenResizePanel)
  );
  const [aiShadowControlPanelVisible, setAiShadowControlPanelVisible] = useState(false);
  const [outpaintControlPanelVisible, setOutpaintControlPanelVisible] = useState(false);
  const [upscaleControlPanelVisible, setUpscaleControlPanelVisible] = useState(false);
  const [passportControlPanelVisible, setPassportControlPanelVisible] = useState(
    () => initialPassportFlow
  );
  const [passportSuggestedCrop, setPassportSuggestedCrop] = useState(null);
  const [passportCountryOptions, setPassportCountryOptions] = useState([]);
  const [passportPresetsLoading, setPassportPresetsLoading] = useState(false);
  const [selectedPassportPreset, setSelectedPassportPreset] = useState(
    () => location?.state?.passportPreset || readPassportFlowSession()?.passportPreset || null
  );
  const [subjectControlPanelVisible, setSubjectControlPanelVisible] = useState(
    () => !initialPassportFlow && !initialOpenResizePanel
  );
  const [layersControlPanelVisible, setLayersControlPanelVisible] = useState(false);
  const [uploadControlPanelVisible, setUploadControlPanelVisible] = useState(false);
  const [, setBackgroundApplied] = useState(false);
  const [layoutModalOpen, setLayoutModalOpen] = useState(false);
  const [layoutOption, setLayoutOption] = useState("option-1");
  const [showPreloader, setShowPreloader] = useState(false);
  const [canvasMeta, setCanvasMeta] = useState({ resolution: "", aspectRatio: "" });
  const [isCanvasPreloading, setIsCanvasPreloading] = useState(false);
  const [passportManualBusy, setPassportManualBusy] = useState(false);
  const [pendingPassportRestore, setPendingPassportRestore] = useState(false);
  const [subjectReady, setSubjectReady] = useState(false);
  const [appliedResizePresetId, setAppliedResizePresetId] = useState(null);
  const [editorResizeMode, setEditorResizeMode] = useState("fit");
  const [lastResizePreset, setLastResizePreset] = useState(null);
  const appliedEntryResizePresetRef = useRef(false);

  /** Subject URL before last AI-background generate (restore on "Remove AI Background"). */
  const subjectUrlBeforeAiRef = useRef(null);

  // Ref to store background color apply timeout
  const bgColorTimeoutRef = useRef(null);

  // Stored background-removed image URL (from API or S3). Used when user selects "Remove Background" in Subject/Background tabs so we restore this instead of re-calling API.
  const isFreshUpload = location?.state?.freshUpload === true;
  // Drop leftover prep session so a later reload does not reopen an empty overlay.
  useEffect(() => {
    if (isFreshUpload) clearToolPrepSession();
  }, [isFreshUpload]);
  const initialOpenToolPrep = isFreshUpload
    ? null
    : resolveOpenToolPrep(location?.state, {
        // sessionStorage-only reopen needs a live upload; after reload ImageContext is empty
        // and reopening leaves a broken popup with no file picker.
        hasLiveImageSource: Boolean(selectedFile || inputPreview),
      });
  const isToolPrepEntry = isToolPrepKey(initialOpenToolPrep);
  // Prep overlay for Editing Tools → Resize | Shadow | Outline | Background | Blur | …
  const [toolPrepKind, setToolPrepKind] = useState(() =>
    isToolPrepEntry ? initialOpenToolPrep : null
  );
  /**
   * Skip the editor's automatic background-removal call while a prep overlay
   * is open, and after Open in Editor, when the tool already handed over a
   * finished image. A normal upload still runs background removal.
   */
  const toolHandedOffImage = Boolean(
    location?.state?.deferBackgroundRemoval ||
      location?.state?.toolPrepApply?.result
  );
  const deferBgRemoval = Boolean(toolPrepKind) || toolHandedOffImage;
  /** dashboard → Close goes to Dashboard; editor → Close dismisses overlay only. */
  const [toolPrepEntryMode, setToolPrepEntryMode] = useState(() =>
    isToolPrepEntry
      ? resolveToolPrepEntryMode(location?.state)
      : null
  );
  const pendingToolPrepApplyRef = useRef(null);
  const appliedToolPrepTransformRef = useRef(false);
  const pendingPaddingApplyRef = useRef(null);
  const appliedPaddingRef = useRef(false);
  const [expandShadowOnMount, setExpandShadowOnMount] = useState(false);
  const [expandOutlineOnMount, setExpandOutlineOnMount] = useState(false);
  const [expandWatermarkOnMount, setExpandWatermarkOnMount] = useState(false);

  // Guard: never leave a tool-prep overlay open with no image to work on
  // (reload mid-prep / stale session after Done). Prep tools with inline upload stay open.
  useEffect(() => {
    if (!toolPrepKind) return;
    if (toolPrepAllowsEmptySource(toolPrepKind)) return;
    const hasSource = Boolean(selectedFile || inputPreview || originalImageUrl);
    if (hasSource) return;
    clearToolPrepSession();
    setToolPrepKind(null);
  }, [toolPrepKind, selectedFile, inputPreview, originalImageUrl]);

  const [bgRemovedImageUrl, setBgRemovedImageUrl] = useState(null);

  // Keep cutout blob referenced in the store so toggling Remove BG off does not revoke it.
  useEffect(() => {
    useEditorStore.getState().setSubjectCutoutUrl?.(bgRemovedImageUrl || null);
  }, [bgRemovedImageUrl]);

  /** Uploads library → editor: run BG remove by default (same as new file upload). */
  const autoRemoveBackgroundOnOpen =
    isFreshUpload || location?.state?.autoRemoveBackground === true;
  const [autoBgRunning, setAutoBgRunning] = useState(false);

  // Taxonomy label for Layers / Object. POST /uploads applies the label in its
  // promise chain; this effect also classifies as fallback and re-syncs assets.
  useEffect(() => {
    if (!selectedFile || !isAuthLoaded || !isSignedIn) return undefined;
    let cancelled = false;
    const file = selectedFile;
    (async () => {
      try {
        if (!useEditorStore.persist.hasHydrated()) {
          await new Promise((resolve) => {
            const unsub = useEditorStore.persist.onFinishHydration(() => {
              unsub?.();
              resolve();
            });
          });
        }
        if (cancelled) return;
        const token = await getToken();
        if (!token || cancelled) return;

        let label = getRememberedObjectLabel(file);

        try {
          const result = await registerLibraryUploadOnce(token, file);
          // Label is applied inside registerLibraryUploadOnce; refresh local copy.
          label =
            getRememberedObjectLabel(file) ||
            (typeof result?.label === "string" &&
            !isFallbackClassifyLabel(result.label)
              ? formatClassifyLabel(result.label)
              : label);
          const rootId =
            result?.root_creation_id ?? result?.creation_id ?? result?.id;
          if (rootId) {
            useEditorStore.getState().upsertUploadAsset?.({
              id: `upload:${rootId}`,
              layerId: null,
              label: label || result?.label || "Object",
              imageUrl: result.output_url || null,
              rootCreationId: Number(rootId),
              createdAt: Date.now(),
            });
            const store = useEditorStore.getState();
            if (!store.rootCreationId) {
              store.setLineage?.({ rootCreationId: Number(rootId) });
            }
          }
        } catch (err) {
          console.warn("[Editor] library upload register failed", err);
        }

        if (!label) {
          label = await classifyImageLabelWithRetry(token, file);
          if (label) {
            rememberObjectLabel(file, label);
            applyObjectLayerLabel(label);
          }
        } else {
          // Re-apply in case layers were reset while upload was in flight.
          applyObjectLayerLabel(label);
        }

        if (cancelled || !label) return;
        const assets = useEditorStore.getState().uploadAssets || [];
        const latest = assets.find((a) =>
          isFallbackClassifyLabel(a.label || "")
        );
        if (latest) {
          useEditorStore.getState().upsertUploadAsset?.({
            ...latest,
            label,
          });
        }
      } catch (err) {
        console.warn("[Editor] classify Object label failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFile, getToken, isAuthLoaded, isSignedIn]);

  const [removeBackgroundActive, setRemoveBackgroundActive] = useState(
    () => autoRemoveBackgroundOnOpen || !isToolPrepEntry
  );
  /** Original (non-cutout) subject URL for Object-tab Remove BG toggle OFF. */
  const originalSubjectUrlRef = useRef(null);

  // Capture first non-cutout source so Object toggle OFF can restore it
  useEffect(() => {
    if (originalSubjectUrlRef.current) return;
    const candidate = originalImageUrl || inputPreview;
    if (candidate) {
      originalSubjectUrlRef.current = candidate;
    }
  }, [originalImageUrl, inputPreview]);

  const transparentSourceId = useEditorStore((s) => s.transparentSourceId);
  const rootCreationId = useEditorStore((s) => s.rootCreationId);

  // Get initial processed image from location.state
  const isDemoSample = location?.state?.demoSample === true;
  const demoSampleCutoutUrl =
    isDemoSample && location?.state?.processedImage
      ? location.state.processedImage
      : null;
  const isDemoPassportSample =
    isDemoSample &&
    !demoSampleCutoutUrl &&
    (location?.state?.editorFlow === "passport" ||
      location?.state?.entrySource === ENTRY_SOURCE.PASSPORT);
  const demoSampleOriginalUrl = isDemoPassportSample
    ? (Array.isArray(location?.state?.images) ? location.state.images[0] : null)
    : null;
  const initialProcessedImage = isDemoSample || isFreshUpload ? null : location?.state?.processedImage || null;
  const [demoSampleLoading, setDemoSampleLoading] = useState(
    () => isDemoSample && Boolean(demoSampleCutoutUrl || demoSampleOriginalUrl)
  );
  const locationPassportPreset = location?.state?.passportPreset || null;
  const creationIdParam = (searchParams.get("creationId") || "").trim();
  const exportIdParam = (searchParams.get("exportId") || "").trim();
  const hasUrlSessionTarget =
    (exportIdParam && Number(exportIdParam) > 0) ||
    (creationIdParam && Number(creationIdParam) > 0);
  const isPassportFlow =
    entrySource === ENTRY_SOURCE.PASSPORT ||
    editorFlow === "passport" ||
    location?.state?.editorFlow === "passport";
  const menuMode = isPassportFlow ? "passport_only" : "standard";

  const handleBackgroundRemovedStored = useCallback((outputUrl, s3Url) => {
    // Prefer blob/data from processing pipeline — never store raw S3 for canvas
    setBgRemovedImageUrl(outputUrl || s3Url);
    // Upload → CanvasEditor: cutout ready → Remove Background stays ON
    setRemoveBackgroundActive(true);
  }, []);

  const {
    isLoading: isBgLoading,
    processedImage: bgProcessedImage,
    creationId: bgCreationId,
    error: bgError,
    retry: retryBg,
    setProcessedImage: setBgProcessedImage,
    setCreationId: setBgCreationId,
  } = useImageProcessing(initialProcessedImage, handleBackgroundRemovedStored, {
    // Fresh uploads must always run even if a prior ?creationId= is already in the URL.
    enabled:
      !isDemoSample &&
      !deferBgRemoval &&
      (!hasUrlSessionTarget || isFreshUpload || Boolean(selectedFile)),
    ignoreProcessedGate: isFreshUpload || Boolean(selectedFile),
  });

  const {
    isLoading: isPassportLoading,
    processedImage: passportProcessedImage,
    creationId: passportCreationId,
    error: passportError,
    suggestedCrop: passportAutoSuggestedCrop,
    retry: retryPassport,
    setProcessedImage: setPassportProcessedImage,
    setCreationId: setPassportCreationId,
    setSuggestedCrop: setPassportAutoSuggestedCrop,
  } = usePassportProcessing({
    enabled: isPassportFlow && !isDemoSample,
    passportPreset: selectedPassportPreset || passportPreset || locationPassportPreset,
    onPassportComplete: handleBackgroundRemovedStored,
  });

  const isLoading = isDemoSample
    ? demoSampleLoading
    : isPassportFlow
      ? isPassportLoading || passportManualBusy
      : isBgLoading;
  const processedImage =
    isDemoSample || !isPassportFlow ? bgProcessedImage : passportProcessedImage;
  const processedImageRef = useRef(processedImage);
  processedImageRef.current = processedImage;
  const [shadowSoftOverflow, setShadowSoftOverflow] = useState(() => {
    const pending = location?.state?.toolPrepApply;
    return (
      pending?.result?.preserveShadowLayout === true ||
      pending?.kind === TOOL_PREP_KEYS.AI_SHADOW
    );
  });
  const creationId = isPassportFlow ? passportCreationId : bgCreationId;
  const error = isPassportFlow ? passportError : bgError;
  const retry = isPassportFlow ? retryPassport : retryBg;
  const setProcessedImage = isPassportFlow ? setPassportProcessedImage : setBgProcessedImage;
  const setCreationId = isPassportFlow ? setPassportCreationId : setBgCreationId;

  const canvasCreationId = storeCreationId ?? creationId;

  const getAiLineage = useCallback(() => {
    const parent = canvasCreationId;
    return {
      parentCreationId: parent,
      transparentSourceId: transparentSourceId ?? parent,
      rootCreationId: rootCreationId ?? parent,
    };
  }, [canvasCreationId, transparentSourceId, rootCreationId]);

  const handleCreationRestored = useCallback(
    (_meta, isPassportCreation) => {
      if (!isPassportCreation) return;
      setPendingPassportRestore(true);
      startPassportFlow(null);
    },
    [startPassportFlow]
  );

  const { restoring: sessionRestoring, restoreError: sessionRestoreError, awaitingRestore } =
    useEditorSessionRestore({
      shareIdParam,
      isDemoSample,
      isFreshUpload,
      isLoading,
      selectedFile,
      processedImage,
      getToken,
      isAuthLoaded,
      setProcessedImage: setBgProcessedImage,
      setCreationId: setBgCreationId,
      setPassportProcessedImage: setPassportProcessedImage,
      setPassportCreationId: setPassportCreationId,
      onCreationMeta: handleCreationRestored,
      setBgRemovedImageUrl,
      setRemoveBackgroundActive,
      // Keep Remove BG ON while we auto-remove opaque Uploads-library opens.
      preferRemoveBackgroundOn: autoRemoveBackgroundOnOpen,
      navigate,
      creationIdParam,
      exportIdParam,
    });

  useEditorSessionCommit({
    creationId,
    processedImage,
    shareIdParam,
    // Still commit after tool-prep / upload once we have a creationId — blob alone
    // cannot survive reload.
    enabled: !isDemoSample,
  });

  /**
   * Auto-BG for Uploads-library opens (and fresh-upload fallback when File is gone).
   * Uses a generation token so React Strict Mode / dep churn cannot cancel-and-lock forever.
   */
  const autoBgGenRef = useRef(0);
  const [autoBgFailed, setAutoBgFailed] = useState(false);

  // New navigation into editor → reset auto-BG attempt state.
  useEffect(() => {
    autoBgGenRef.current += 1;
    setAutoBgFailed(false);
    setAutoBgRunning(false);
  }, [location.key]);

  useEffect(() => {
    if (!autoRemoveBackgroundOnOpen) return;
    if (isPassportFlow || isDemoSample || shareIdParam || deferBgRemoval) return;
    if (!isAuthLoaded || !isSignedIn) return;
    if (exportIdParam) return;
    if (sessionRestoring || isBgLoading) return;
    if (bgRemovedImageUrl) return;
    if (autoBgFailed) return;

    // Fresh file path: useImageProcessing owns the first attempt while File is present.
    if (isFreshUpload && selectedFile) return;

    const sourceFile = selectedFile;
    const sourceUrl =
      originalSubjectUrlRef.current ||
      originalImageUrl ||
      inputPreview ||
      processedImage;
    if (!sourceFile && !sourceUrl) return;

    const gen = ++autoBgGenRef.current;
    let cancelled = false;

    (async () => {
      try {
        setAutoBgRunning(true);
        setRemoveBackgroundActive(true);
        setAutoBgFailed(false);

        const token = await getToken();
        if (!token || cancelled || gen !== autoBgGenRef.current) return;

        const store = useEditorStore.getState();
        const latestUrl =
          originalSubjectUrlRef.current ||
          originalImageUrl ||
          inputPreview ||
          processedImage ||
          sourceUrl;
        if (latestUrl && !originalSubjectUrlRef.current) {
          originalSubjectUrlRef.current = latestUrl;
        }

        const { cutoutUrl, creationId: cutoutCreationId } =
          await ensureBackgroundRemoved({
            token,
            imageFile: sourceFile,
            imageUrl: latestUrl,
            cachedCutoutUrl: null,
            rootCreationId: store.rootCreationId,
            // Prefer upload root as input; current creation may be the opaque original.
            creationId: store.rootCreationId || store.creationId,
            lineage: store.rootCreationId
              ? {
                  root_creation_id: store.rootCreationId,
                  parent_creation_id: store.rootCreationId,
                }
              : null,
          });

        if (cancelled || gen !== autoBgGenRef.current) return;

        if (cutoutCreationId != null) {
          const cid = Number(cutoutCreationId);
          store.setLineage?.({
            transparentSourceId: cid,
            creationId: cid,
            rootCreationId: store.rootCreationId || cid,
          });
          setBgCreationId(cid);
        }

        setBgRemovedImageUrl(cutoutUrl);
        applyManualEdit({ imageUrl: cutoutUrl });
        setProcessedImage(cutoutUrl);
        setRemoveBackgroundActive(true);
      } catch (err) {
        if (cancelled || gen !== autoBgGenRef.current) return;
        console.error("[Editor] auto BG on upload failed", err);
        setAutoBgFailed(true);
        setRemoveBackgroundActive(false);
        alert(
          err?.message || "Failed to remove background. Please try again."
        );
      } finally {
        if (gen === autoBgGenRef.current) {
          setAutoBgRunning(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    applyManualEdit,
    autoBgFailed,
    autoRemoveBackgroundOnOpen,
    bgRemovedImageUrl,
    deferBgRemoval,
    exportIdParam,
    getToken,
    inputPreview,
    isAuthLoaded,
    isBgLoading,
    isDemoSample,
    isFreshUpload,
    isPassportFlow,
    isSignedIn,
    originalImageUrl,
    processedImage,
    selectedFile,
    sessionRestoring,
    setBgCreationId,
    setProcessedImage,
    shareIdParam,
  ]);

  useEditorUndoSync({
    setProcessedImage,
    setCreationId,
    getToken,
    canvasEditorRef,
    enabled: !shareIdParam && Boolean(processedImage) && !isDemoSample,
  });

  const subjectUrlForApis = processedImage || originalProcessedImage;
  const studio = useStudioFeatures(subjectUrlForApis, getAiLineage);

  const requireLoginForAi = useCallback(() => {
    if (isSharedGuest) {
      navigate("/login");
      return false;
    }
    return true;
  }, [isSharedGuest, navigate]);

  /** Resolve S3 output via creation API blob, then commit AI undo snapshot. */
  const applyAiTaskResult = useCallback(
    async ({
      outputUrl,
      creationId: rawCreationId,
      background,
      expectedGeneration,
      updateOriginal = false,
    }) => {
      const token = await getToken();
      if (!token) return false;

      const { displayUrl, creationId: resolvedCid } =
        await resolveCreationImageUrl(token, outputUrl, rawCreationId);

      const applied = applyAiResult({
        imageUrl: displayUrl,
        creationId: resolvedCid,
        background,
        expectedGeneration,
      });

      if (applied) {
        setProcessedImage(displayUrl);
        if (resolvedCid) {
          setCreationId(resolvedCid);
        }
        if (updateOriginal) {
          setOriginalProcessedImage(displayUrl);
        }
        setRemoveBackgroundActive(false);
      }
      return applied;
    },
    [
      getToken,
      applyAiResult,
      setProcessedImage,
      setCreationId,
      setOriginalProcessedImage,
    ]
  );

  const handleAiMenuBlocked = useCallback(() => {
    if (isSharedGuest) {
      navigate("/login");
      return true;
    }
    return false;
  }, [isSharedGuest, navigate]);

  useEffect(() => {
    if (!shareIdParam) {
      setShareLoading(false);
      return;
    }

    let cancelled = false;
    setShareLoading(true);
    setShareError(null);

    (async () => {
      try {
        const data = await getSharedImage(shareIdParam);
        if (cancelled) return;
        if (!data?.image_url) {
          throw new Error("Shared image is unavailable.");
        }
        setProcessedImage(data.image_url);
        setOriginalProcessedImage(data.image_url);
        setImages([data.image_url]);
        setOriginalImageUrl(data.image_url);
        if (data.shared_by_name) {
          setSharedByName(data.shared_by_name);
        }
      } catch (err) {
        if (!cancelled) {
          logger.error("Failed to load shared design", err);
          setShareError(
            err?.response?.data?.detail ||
              err?.message ||
              "Could not load shared design."
          );
        }
      } finally {
        if (!cancelled) {
          setShareLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shareIdParam, setProcessedImage]);

  // Preloader is now rendered in JSX below

  // Show preloader while loading; hide when loading completes.
  // Default BG opens use the workspace loader only (no stacked overlay = no double "Processing…").
  useEffect(() => {
    if (autoRemoveBackgroundOnOpen || autoBgRunning) {
      setShowPreloader(false);
      return;
    }
    setShowPreloader(isLoading || shareLoading || sessionRestoring);
  }, [
    autoBgRunning,
    autoRemoveBackgroundOnOpen,
    isLoading,
    shareLoading,
    sessionRestoring,
  ]);

  // Initialize images from location state or context
  const locationImages = location?.state?.images;
  useEffect(() => {
    const previewFromState = Array.isArray(locationImages) ? locationImages : [];

    if (inputPreview) {
      setImages([inputPreview]);
      setOriginalImageUrl(inputPreview);
    } else if (previewFromState.length > 0) {
      setImages(previewFromState);
      setOriginalImageUrl(previewFromState[0]);
    }
  }, [locationImages, inputPreview]);

  useEffect(() => {
    const fromLocation =
      location?.state?.editorFlow === "passport" && locationPassportPreset;
    const fromSession = passportPreset && editorFlow === "passport";

    if (fromLocation) {
      startPassportFlow(locationPassportPreset);
      setSelectedPassportPreset(locationPassportPreset);
      setPassportPreset(locationPassportPreset);
    } else if (fromSession && !selectedPassportPreset) {
      setSelectedPassportPreset(passportPreset);
    }
  }, [
    location?.state?.editorFlow,
    locationPassportPreset,
    startPassportFlow,
    setPassportPreset,
    passportPreset,
    editorFlow,
    selectedPassportPreset,
  ]);

  useEffect(() => {
    if (
      location?.state?.entrySource &&
      isResizeEntrySource(location.state.entrySource) &&
      locationEntryResizePreset
    ) {
      startResizeEntryFlow(locationEntryResizePreset, location.state.entrySource);
    }
  }, [
    location?.state?.entrySource,
    locationEntryResizePreset,
    startResizeEntryFlow,
  ]);

  const handleSubjectReady = useCallback(() => {
    setSubjectReady(true);
  }, []);

  const handleCanvasNaturalSize = useCallback(({ width, height }) => {
    setCanvasMeta((prev) => {
      // Keep an explicit resize/export size if one was already applied.
      if (prev?.resolution) return prev;
      return {
        resolution: formatPixelDimensions(width, height),
        aspectRatio: formatAspectRatioLabel(width, height),
      };
    });
  }, []);

  useEffect(() => {
    setSubjectReady(false);
    appliedEntryResizePresetRef.current = false;
    appliedToolPrepTransformRef.current = false;
    appliedPaddingRef.current = false;
    setAppliedResizePresetId(null);
    setLastResizePreset(null);
    setCanvasMeta({ resolution: "", aspectRatio: "" });
  }, [processedImage]);

  useEffect(() => {
    if (!isPassportFlow) {
      return undefined;
    }
    let cancelled = false;
    setPassportPresetsLoading(true);
    getPassportPresets()
      .then((res) => {
        if (cancelled || !res?.success || !Array.isArray(res.data)) return;
        const passportCat = res.data.find((c) => c.category_id === PASSPORT_CATEGORY_ID);
        const presets = passportCat?.presets || (res.data[0]?.presets ? res.data[0].presets : res.data);
        setPassportCountryOptions(sortPassportPresets(Array.isArray(presets) ? presets : []));
      })
      .catch(() => {
        if (!cancelled) setPassportCountryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setPassportPresetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPassportFlow]);

  // Match entry preset (e.g. India from dashboard) to full API list so flag icon + metadata show.
  useEffect(() => {
    if (!isPassportFlow || !passportCountryOptions.length) return;

    const currentId =
      selectedPassportPreset?.id ||
      passportPreset?.id ||
      locationPassportPreset?.id;
    if (!currentId) return;

    const matched = passportCountryOptions.find((p) => p.id === currentId);
    if (!matched) return;

    if (
      selectedPassportPreset?.id !== matched.id ||
      selectedPassportPreset?.icon_url !== matched.icon_url
    ) {
      setSelectedPassportPreset(matched);
      setPassportPreset(matched);
    }
  }, [
    isPassportFlow,
    passportCountryOptions,
    passportPreset?.id,
    locationPassportPreset?.id,
    selectedPassportPreset?.id,
    selectedPassportPreset?.icon_url,
    setPassportPreset,
  ]);

  useEffect(() => {
    if (!pendingPassportRestore || !passportCountryOptions.length || !processedImage) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const dims = await getImageDimensions(processedImage);
        if (cancelled) return;
        const matched = findPassportPresetByDimensions(
          passportCountryOptions,
          dims.width,
          dims.height
        );
        const preset = matched || passportCountryOptions[0] || null;
        if (preset) {
          startPassportFlow(preset);
          setSelectedPassportPreset(preset);
          setPassportPreset(preset);
        }
      } catch {
        const fallback = passportCountryOptions[0] || null;
        if (!cancelled && fallback) {
          startPassportFlow(fallback);
          setSelectedPassportPreset(fallback);
          setPassportPreset(fallback);
        }
      } finally {
        if (!cancelled) {
          setPendingPassportRestore(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    pendingPassportRestore,
    passportCountryOptions,
    processedImage,
    startPassportFlow,
    setPassportPreset,
  ]);

  useEffect(() => {
    if (passportAutoSuggestedCrop) {
      setPassportSuggestedCrop(passportAutoSuggestedCrop);
    }
  }, [passportAutoSuggestedCrop]);

  const handlePassportCountryChange = useCallback((preset) => {
    setSelectedPassportPreset(preset);
    setPassportPreset(preset);
    setPassportSuggestedCrop(null);
    setPassportAutoSuggestedCrop(null);
  }, [setPassportPreset, setPassportAutoSuggestedCrop]);

  // Store the original processed image (from API) when it first arrives
  useEffect(() => {
    if (processedImage && !originalProcessedImage) {
      // Store the first processed image as the original (transparent background)
      // This will be our base for all background color changes
      setOriginalProcessedImage(processedImage);
    }
  }, [processedImage, originalProcessedImage]);

  // Also store initial processed image if provided from location.state
  useEffect(() => {
    if (initialProcessedImage && !originalProcessedImage) {
      setOriginalProcessedImage(initialProcessedImage);
    }
  }, [initialProcessedImage, originalProcessedImage]);

  // Demo samples: preload CDN asset with a short simulated processing delay (no API/credits).
  useEffect(() => {
    const preloadUrl = demoSampleCutoutUrl || demoSampleOriginalUrl;
    if (!isDemoSample || !preloadUrl) {
      setDemoSampleLoading(false);
      return;
    }

    let cancelled = false;
    let revealTimerId = null;
    const minDelayMs = 2000;
    const startedAt = Date.now();

    setDemoSampleLoading(true);

    const revealSample = () => {
      if (cancelled) return;
      if (demoSampleCutoutUrl) {
        setBgProcessedImage(demoSampleCutoutUrl);
        setBgRemovedImageUrl(demoSampleCutoutUrl);
        setOriginalProcessedImage(demoSampleCutoutUrl);
        setRemoveBackgroundActive(true);
      }
      setDemoSampleLoading(false);
      setShowPreloader(false);
    };

    const scheduleReveal = () => {
      const waitMs = Math.max(0, minDelayMs - (Date.now() - startedAt));
      revealTimerId = window.setTimeout(revealSample, waitMs);
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = scheduleReveal;
    img.onerror = scheduleReveal;
    img.src = preloadUrl;

    return () => {
      cancelled = true;
      if (revealTimerId) {
        window.clearTimeout(revealTimerId);
      }
    };
  }, [
    isDemoSample,
    demoSampleCutoutUrl,
    demoSampleOriginalUrl,
    setBgProcessedImage,
  ]);

  // Computed values
  const originalImg = originalImageUrl || inputPreview || (images && images.length > 0 ? images[0] : null);
  const { openToolPrepFromEditor, toolPrepNode } = useEditorToolPrep({
    appliedPaddingRef,
    appliedToolPrepTransformRef,
    applyManualEdit,
    background,
    bgRemovedImageUrl,
    canvasEditorRef,
    clearImage,
    creationId,
    inputPreview,
    location,
    navigate,
    originalImageUrl,
    originalImg,
    originalSubjectUrlRef,
    pendingPaddingApplyRef,
    pendingToolPrepApplyRef,
    processedImage,
    processedImageRef,
    removeBackgroundActive,
    rootCreationId,
    seedHistoryBaseline,
    selectedFile,
    setActiveMenuIndex,
    setAppliedResizePresetId,
    setBackground,
    setBackgroundApplied,
    setBackgroundControlPanelVisible,
    setBgCreationId,
    setBgRemovedImageUrl,
    setCanvasMeta,
    setEditorResizeMode,
    setExpandOutlineOnMount,
    setExpandShadowOnMount,
    setExpandWatermarkOnMount,
    setIsCanvasPreloading,
    setLastResizePreset,
    setLayersControlPanelVisible,
    setOriginalProcessedImage,
    setOutline,
    setProcessedImage,
    setRemoveBackgroundActive,
    setResizeControlPanelVisible,
    setShadow,
    setShadowSoftOverflow,
    setShowPreloader,
    setSubjectControlPanelVisible,
    setToolPrepEntryMode,
    setToolPrepKind,
    setUploadControlPanelVisible,
    setWatermark,
    storeCreationId,
    subjectReady,
    toolPrepEntryMode,
    toolPrepKind,
    transparentSourceId,
    updateObject,
  });
  const objectMenuThumbnail =
    selectedImageLayer?.imageUrl || processedImage || originalImg;
  const onObjectTransformChange = useCanvasToolSync(
    canvasEditorRef,
    processedImage || originalImg
  );
  const { imageSize } = useImageSize(originalImg);
  /**
   * Processing loader frame must match CanvasEditor's fitted canvas size
   * (80% of workspace × image AR) — never a separate hardcoded square/wide box.
   */
  const [canvasLoaderSize, setCanvasLoaderSize] = useState(null);
  const loaderSourceUrl =
    originalImg || inputPreview || processedImage || null;

  useLayoutEffect(() => {
    let cancelled = false;

    const measure = async () => {
      const el = workspaceRef.current;
      if (!el) return;
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (!cw || !ch) return;

      let aspectRatio = 1;
      if (loaderSourceUrl) {
        try {
          const dims = await getImageDimensions(loaderSourceUrl);
          if (cancelled) return;
          if (dims?.width > 0 && dims?.height > 0) {
            aspectRatio = dims.width / dims.height;
          }
        } catch {
          aspectRatio = imageSize?.width && imageSize?.height
            ? imageSize.width / imageSize.height
            : 1;
        }
      } else if (imageSize?.width && imageSize?.height) {
        aspectRatio = imageSize.width / imageSize.height;
      }

      const fitted = computeFittedCanvasSize(cw, ch, aspectRatio);
      if (!cancelled && fitted) {
        setCanvasLoaderSize(fitted);
      }
    };

    measure();
    const el = workspaceRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return () => {
        cancelled = true;
      };
    }
    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(el);
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [loaderSourceUrl, imageSize?.width, imageSize?.height]);

  const canvasLoaderStyle = canvasLoaderSize
    ? {
        width: `${canvasLoaderSize.width}px`,
        height: `${canvasLoaderSize.height}px`,
      }
    : { width: "min(72vw, 640px)", height: "min(72vw, 640px)" };

  const showPanelsForMenu = useCallback((menuIndex) => {
    setSubjectControlPanelVisible(menuIndex === 0);
    setBackgroundControlPanelVisible(menuIndex === 1);
    setResizeControlPanelVisible(menuIndex === 2);
    setAiShadowControlPanelVisible(false);
    if (IS_APP_V1) {
      setOutpaintControlPanelVisible(false);
      setUpscaleControlPanelVisible(false);
    } else {
      setOutpaintControlPanelVisible(menuIndex === 4);
      setUpscaleControlPanelVisible(menuIndex === 5);
    }
    setPassportControlPanelVisible(menuIndex === 6);
    setLayersControlPanelVisible(menuIndex === 7);
    setUploadControlPanelVisible(menuIndex === 8);
  }, []);

  useEffect(() => {
    if (isPassportFlow) {
      setActiveMenuIndex(6);
      showPanelsForMenu(6);
      return;
    }
    // Marketplace / dating / social resize entry → classic left Resize panel + auto BG
    if (isResizeEntryFlow && initialOpenToolPrep !== TOOL_PREP_KEYS.RESIZE) {
      setActiveMenuIndex(2);
      showPanelsForMenu(2);
    }
  }, [isPassportFlow, isResizeEntryFlow, initialOpenToolPrep, showPanelsForMenu]);

  const handleSidebarMenuChange = useCallback((menuIndex) => {
    showPanelsForMenu(menuIndex);
    if (menuIndex === 4) {
      openToolPrepFromEditor(TOOL_PREP_KEYS.AI_EXPAND);
    } else if (menuIndex === 5) {
      openToolPrepFromEditor(TOOL_PREP_KEYS.AI_UPSCALE);
    }
    if (menuIndex === 0) {
      const sel = useEditorStore.getState().selectedLayerId;
      if (sel && sel !== "object" && sel !== "background") {
        canvasEditorRef.current?.selectOverlay?.(sel);
      } else {
        selectLayer("object");
        canvasEditorRef.current?.selectObject?.();
      }
    } else if (menuIndex === 1) {
      selectLayer("background");
      canvasEditorRef.current?.selectCanvas?.();
    } else if (menuIndex === 7) {
      // Layers panel — keep current canvas selection until a row is clicked
    }
  }, [showPanelsForMenu, selectLayer, openToolPrepFromEditor]);

  const handleCanvasClick = () => {
    if (isPassportFlow) return;
    selectLayer("background");
    setActiveMenuIndex(1);
    showPanelsForMenu(1);
    canvasEditorRef.current?.selectCanvas?.();
  };

  const handleObjectClick = () => {
    if (isPassportFlow) return;
    selectLayer("object");
    setActiveMenuIndex(0);
    showPanelsForMenu(0);
    canvasEditorRef.current?.selectObject?.();
  };

  const handleWorkspaceMarginClick = () => {
    if (isPassportFlow) return;
    setActiveMenuIndex(7);
    showPanelsForMenu(7);
  };

  const handleLayersSelect = useCallback(
    (layerId) => {
      if (layerId === "object") {
        selectLayer("object");
        canvasEditorRef.current?.selectObject?.();
        setActiveMenuIndex(0);
        showPanelsForMenu(0);
      } else if (layerId === "background") {
        selectLayer("background");
        canvasEditorRef.current?.selectCanvas?.();
        setActiveMenuIndex(1);
        showPanelsForMenu(1);
      } else {
        // Image overlay — pick bounding box on canvas; open Object tools for transforms
        selectLayer(layerId);
        canvasEditorRef.current?.selectOverlay?.(layerId);
        setActiveMenuIndex(0);
        showPanelsForMenu(0);
        // Re-apply after menu sync effect (same tick / delayed selectObject race)
        window.requestAnimationFrame(() => {
          canvasEditorRef.current?.selectOverlay?.(layerId);
        });
      }
    },
    [selectLayer, showPanelsForMenu]
  );

  const handleRemoveObjectLayer = useCallback(() => {
    useEditorStore.getState().setImageUrl(null);
    useEditorStore.getState().setSubjectCutoutUrl?.(null);
    useEditorStore.getState().resetObjectState?.();
    setProcessedImage(null);
    setOriginalProcessedImage(null);
    setBgRemovedImageUrl(null);
    setRemoveBackgroundActive(false);
    selectLayer("background");
  }, [selectLayer, setProcessedImage]);

  const handleOverlayClick = useCallback(
    (layerId) => {
      if (isPassportFlow) return;
      selectLayer(layerId);
      setActiveMenuIndex(0);
      showPanelsForMenu(0);
    },
    [isPassportFlow, selectLayer, showPanelsForMenu]
  );

  const handleUploadPlaced = useCallback(
    (layerId) => {
      selectLayer(layerId);
      canvasEditorRef.current?.selectOverlay?.(layerId);
      // Stay on Upload tab — do not jump to Layers
    },
    [selectLayer]
  );

  const handleAlign = (mode) => {
    const store = useEditorStore.getState();
    const selId = store.selectedLayerId;
    const imageLayer =
      selId && selId !== "object" && selId !== "background"
        ? store.layers.find((l) => l.id === selId && l.type === "image")
        : null;

    if (imageLayer) {
      const canvasSize = canvasEditorRef.current?.getCanvasSize?.();
      const next = alignImageLayer(imageLayer, canvasSize, mode);
      if (next) {
        store.updateImageLayer(imageLayer.id, next);
      }
      return;
    }

    if (!canvasEditorRef.current) return;

    if (mode === "middle") {
      canvasEditorRef.current.alignObjectMiddle();
    } else if (mode === "center") {
      canvasEditorRef.current.alignObjectCenter();
    } else if (mode === "left") {
      canvasEditorRef.current.alignObjectLeft();
    } else if (mode === "right") {
      canvasEditorRef.current.alignObjectRight();
    } else if (mode === "top") {
      canvasEditorRef.current.alignObjectTop();
    } else if (mode === "bottom") {
      canvasEditorRef.current.alignObjectBottom();
    } else if (mode === "topLeft") {
      canvasEditorRef.current.alignObjectTopLeft();
    } else if (mode === "topRight") {
      canvasEditorRef.current.alignObjectTopRight();
    } else if (mode === "bottomLeft") {
      canvasEditorRef.current.alignObjectBottomLeft();
    } else if (mode === "bottomRight") {
      canvasEditorRef.current.alignObjectBottomRight();
    }
  };

  const {
    applyBackgroundColor,
    removeBackgroundColor,
    applyBackgroundImage,
    removeBackgroundImage,
    handleApplyAiBackground,
    handleAiBackgroundPromptGenerate,
    handleRestoreBackgroundRemoved,
    handleRemoveBackgroundChange,
    removeAiBackground,
    handleUnsplashSearch,
    handleUnsplashTrackDownload,
    handleApplyAiShadowApi,
    handleApplyUpscale,
    handleApplyOutpaint,
    handleSuggestPassportCrop,
    handleGeneratePassport,
    applyEditorResizePreset,
    handleResizePreset,
    handleEditorResizeModeChange,
  } = useEditorStageActions({
    applyAiResult,
    applyAiTaskResult,
    applyManualEdit,
    background,
    bgRemovedImageUrl,
    bgColorTimeoutRef,
    bumpAiGeneration,
    canvasEditorRef,
    creationId,
    editorResizeMode,
    getAiLineage,
    getToken,
    inputPreview,
    isPassportFlow,
    lastResizePreset,
    layers,
    originalImageUrl,
    originalProcessedImage,
    originalSubjectUrlRef,
    processedImage,
    requireLoginForAi,
    resetBackground,
    selectedFile,
    selectedLayerId,
    setAppliedResizePresetId,
    setBackground,
    setBackgroundApplied,
    setBgRemovedImageUrl,
    setCanvasMeta,
    setCreationId,
    setEditorResizeMode,
    setIsCanvasPreloading,
    setLastResizePreset,
    setLayerVisibility,
    setOriginalProcessedImage,
    setPassportManualBusy,
    setPassportSuggestedCrop,
    setProcessedImage,
    setRemoveBackgroundActive,
    setShadow,
    studio,
    subjectUrlBeforeAiRef,
    transparentSourceId,
    updateImageLayer,
  });
  // Apply dashboard resize/trending entry preset once canvas + subject are ready
  useEffect(() => {
    if (
      isPassportFlow ||
      !shouldApplyEntryPreset ||
      !activeEntryResizePreset ||
      !processedImage ||
      !subjectReady ||
      appliedEntryResizePresetRef.current
    ) {
      return;
    }
    if (!canvasEditorRef.current?.isSubjectReady?.()) {
      return;
    }
    applyEditorResizePreset(
      normalizePresetForResizeEditor(activeEntryResizePreset),
      editorResizeMode
    );
    appliedEntryResizePresetRef.current = true;
    if (activeEntryResizePreset.id != null) {
      setAppliedResizePresetId(activeEntryResizePreset.id);
    }
  }, [
    isPassportFlow,
    shouldApplyEntryPreset,
    activeEntryResizePreset,
    processedImage,
    subjectReady,
    applyEditorResizePreset,
    editorResizeMode,
  ]);

  // Hide preloader when canvas is fully initialized and rendered
  const hidePreloader = useCallback(() => {
    setShowPreloader(false);
    const bg = useEditorStore.getState().background;
    if (
      bg.type === "image" &&
      bg.imageUrl &&
      canvasEditorRef.current?.setBackgroundImage
    ) {
      canvasEditorRef.current.setBackgroundImage(bg.imageUrl);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (bgColorTimeoutRef.current) {
        clearTimeout(bgColorTimeoutRef.current);
      }
    };
  }, []);

  const displayError = shareError || error || sessionRestoreError;
  /** Keep a single loader until the default cutout is ready — no original flash / AR flicker. */
  const awaitingDefaultBgCutout =
    autoRemoveBackgroundOnOpen &&
    !isPassportFlow &&
    !isDemoSample &&
    !shareIdParam &&
    !bgRemovedImageUrl &&
    !autoBgFailed;
  const displayLoading =
    isLoading ||
    shareLoading ||
    sessionRestoring ||
    autoBgRunning ||
    awaitingDefaultBgCutout ||
    (awaitingRestore && isAuthLoaded && !sessionRestoreError);
  const canRenderCanvas = Boolean(processedImage || originalImg);

  // Seed undo baseline once canvas is visible (not while preloader shows).
  useEffect(() => {
    if (
      historySeeded ||
      shareIdParam ||
      displayLoading ||
      showPreloader ||
      !processedImage
    ) {
      return;
    }
    seedHistoryBaseline(processedImage);
  }, [
    historySeeded,
    shareIdParam,
    displayLoading,
    showPreloader,
    processedImage,
    seedHistoryBaseline,
  ]);

  // Re-apply classify label after BG processing — layers may have been reset
  // while POST /uploads was still in flight.
  useEffect(() => {
    if (!selectedFile || displayLoading || !processedImage) return;
    const label = getRememberedObjectLabel(selectedFile);
    if (label) applyObjectLayerLabel(label);
  }, [selectedFile, processedImage, displayLoading]);

  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    undo();
  }, [canUndo, undo]);

  const handleRedo = useCallback(() => {
    if (!canRedo) return;
    redo();
  }, [canRedo, redo]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") {
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (e.key === "z" && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      } else if (e.key === "y") {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleUndo, handleRedo]);

  // Re-sync canvas selection once the editor image is ready (ref was null on first menu mount).
  // Do not steal selection from overlay image layers when Object tab is open.
  useEffect(() => {
    if (displayLoading || !processedImage) {
      return;
    }
    if (isPassportFlow) {
      setActiveMenuIndex(6);
      showPanelsForMenu(6);
      return;
    }
    const timer = setTimeout(() => {
      const sel = useEditorStore.getState().selectedLayerId;
      if (activeMenuIndex === 0) {
        if (sel && sel !== "object" && sel !== "background") {
          canvasEditorRef.current?.selectOverlay?.(sel);
        } else {
          canvasEditorRef.current?.selectObject?.();
        }
      } else if (activeMenuIndex === 1) {
        canvasEditorRef.current?.selectCanvas?.();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [processedImage, displayLoading, activeMenuIndex, isPassportFlow, showPanelsForMenu]);

  return (
    <ToolPrepCloseContext.Provider
      value={
        toolPrepEntryMode === TOOL_PREP_ENTRY.EDITOR ? "editor" : "dashboard"
      }
    >
    <WatermarkToolbarOverlayProvider leftPanelSelector=".editor-left-menu-subject-controls-wrapper">
    <div className={`editor-page editor-page--layout-${layoutOption}`}>
      {isShareMode && sharedByName && !shareError && (
        <div className="editor-share-banner" role="status">
          Shared by {sharedByName}
          {isSharedGuest && (
            <span className="editor-share-banner__hint">
              {" "}
              — Sign in to use AI features
            </span>
          )}
        </div>
      )}
      <EditorLeftMenu 
        currentActiveItem={handleSidebarMenuChange}
        activeMenuIndex={activeMenuIndex}
        setActiveMenuIndex={setActiveMenuIndex}
        uploadedImage={objectMenuThumbnail}
        onAiMenuBlocked={handleAiMenuBlocked}
        menuMode={menuMode}
        hideMoreMenu={IS_APP_V1}
        hideSecondaryAiMenus={IS_APP_V1}
      />
      {subjectControlPanelVisible && (
        <EditorLeftMenuSubjectControls 
          onAlign={handleAlign}
          onRestoreBackgroundRemoved={handleRestoreBackgroundRemoved}
          removeBackgroundActive={
            selectedImageLayer
              ? Boolean(selectedImageLayer.bgRemoved)
              : removeBackgroundActive
          }
          onRemoveBackgroundChange={handleRemoveBackgroundChange}
          shadowDefaultExpanded={expandShadowOnMount}
          outlineDefaultExpanded={expandOutlineOnMount}
          watermarkDefaultExpanded={expandWatermarkOnMount}
        />
      )}
      {backgroundControlPanelVisible && (
        <EditorLeftMenuBackgroundControls 
          onApplyBackground={applyBackgroundColor}
          onRemoveBackground={removeBackgroundColor}
          onApplyImageBackground={applyBackgroundImage}
          onApplyAiBackground={handleApplyAiBackground}
          onRemoveImageBackground={removeBackgroundImage}
          onRemoveAiBackground={removeAiBackground}
          backgroundState={background}
          subjectPreviewUrl={originalProcessedImage || processedImage}
          onAiBackgroundPromptGenerate={handleAiBackgroundPromptGenerate}
          aiBackgroundGenerating={studio.busy}
          aiBackgroundError={studio.error}
          onUnsplashSearch={handleUnsplashSearch}
          onUnsplashTrackDownload={handleUnsplashTrackDownload}
        />
      )}
      {resizeControlPanelVisible && (
        <EditorLeftMenuResizeControls
          onResizeSelect={handleResizePreset}
          onResizeModeChange={handleEditorResizeModeChange}
          selectedPresetId={appliedResizePresetId}
          selectedPreset={lastResizePreset}
          resizeMode={editorResizeMode}
          previewImageUrl={processedImage || originalImg}
        />
      )}
      {aiShadowControlPanelVisible && (
        <EditorLeftMenuAiShadowControls
          onApplyAiShadowCloudinary={handleApplyAiShadowApi}
          shadowBusy={studio.busy}
          shadowError={studio.error}
        />
      )}
      {outpaintControlPanelVisible && (
        <EditorLeftMenuOutpaintControls
          onApplyOutpaint={handleApplyOutpaint}
          busy={studio.busy}
          error={studio.error}
        />
      )}
      {upscaleControlPanelVisible && (
        <EditorLeftMenuUpscaleControls
          onApplyUpscale={handleApplyUpscale}
          busy={studio.busy}
          error={studio.error}
        />
      )}
      {passportControlPanelVisible && (
        <EditorLeftMenuPassportControls
          onSuggestCrop={handleSuggestPassportCrop}
          onGenerate={handleGeneratePassport}
          busy={studio.busy || isPassportLoading || passportManualBusy}
          error={studio.error || passportError}
          suggestedCrop={passportSuggestedCrop}
          selectedPreset={selectedPassportPreset || passportPreset || locationPassportPreset}
          countryOptions={passportCountryOptions}
          presetsLoading={passportPresetsLoading}
          onCountryChange={handlePassportCountryChange}
        />
      )}
      {layersControlPanelVisible && (
        <EditorLeftMenuLayersControls
          onSelectLayer={handleLayersSelect}
          onRemoveObject={handleRemoveObjectLayer}
          objectPreviewUrl={processedImage || originalImg}
          backgroundPreview={background}
        />
      )}
      {uploadControlPanelVisible && (
        <EditorLeftMenuUploadControls
          getCanvasSize={() => canvasEditorRef.current?.getCanvasSize?.()}
          onPlacedLayer={handleUploadPlaced}
        />
      )}
      <LayoutSettings 
        isOpen={layoutModalOpen}
        onClose={() => setLayoutModalOpen(false)}
        layoutOption={layoutOption}
        setLayoutOption={setLayoutOption}
      />
      <EditorHeader
        processedImage={processedImage}
        isSharedGuest={isSharedGuest}
        canUndo={canUndo && !isSharedGuest}
        canRedo={canRedo && !isSharedGuest}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onExportSnapshot={(exportSize) => {
          if (!canvasEditorRef.current?.exportCompositePng) {
            return Promise.reject(new Error("Editor is not ready for export."));
          }
          return canvasEditorRef.current.exportCompositePng({ exportSize });
        }}
        onOpenLayoutSettings={() => setLayoutModalOpen(true)}
        hideBeforeAfterCompare={IS_APP_V1}
      />
      <div className="editor-page__workspace" ref={workspaceRef}>
        {displayLoading ? (
          <div className="editor-page__result-container">
            <div className="editor-page__canvas-loader-frame" style={canvasLoaderStyle}>
              <AiProcessingLoader />
            </div>
          </div>
        ) : displayError ? (
          <div className="editor-page__result-container">
            <div className="error-container">
              <div className="error-icon">❌</div>
              <p className="error-message">{displayError}</p>
              <div className="error-actions">
                {!shareError && (
                  <button
                    className="retry-btn"
                    onClick={() => {
                      retry();
                    }}
                  >
                    Retry
                  </button>
                )}
                <button
                  className="back-btn"
                  onClick={() => navigate(isSharedGuest ? "/login" : "/")}
                >
                  {isSharedGuest ? "Back to Home" : "Back to Dashboard"}
                </button>
              </div>
            </div>
          </div>
        ) : !canRenderCanvas ? (
          <div className="editor-page__result-container">
            <div className="error-container">
              <p className="error-message">No image loaded.</p>
              <div className="error-actions">
                <button
                  className="back-btn"
                  onClick={() => navigate(isSharedGuest ? "/login" : "/")}
                >
                  {isSharedGuest ? "Back to Home" : "Back to Dashboard"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="editor-page__workspace-inner">
            <EditorTextFormatToolbar />
            <div className="editor-page__canvas-stack">
            <CanvasEditor
              ref={canvasEditorRef}
              imageUrl={processedImage || originalImg}
              softOverflow={shadowSoftOverflow}
              creationId={canvasCreationId}
              backgroundColor={
                background.type === "color" ? background.color : "transparent"
              }
              backgroundLayerVisible={backgroundLayerVisible}
              objectLayerVisible={objectLayerVisible}
              onCanvasReady={hidePreloader}
              onSubjectReady={handleSubjectReady}
              onNaturalSize={handleCanvasNaturalSize}
              onCanvasClick={handleCanvasClick}
              onObjectClick={handleObjectClick}
              onOverlayClick={handleOverlayClick}
              onWorkspaceMarginClick={handleWorkspaceMarginClick}
              onObjectTransformChange={onObjectTransformChange}
              isCanvasPreloading={isCanvasPreloading}
              resolutionLabel={canvasMeta.resolution}
              aspectRatioLabel={canvasMeta.aspectRatio}
              lockObjectTransform={isPassportFlow}
            />
            {background?.type === "image" &&
            background?.attribution?.photographer ? (
              <p className="editor-page__unsplash-credit">
                Photo by{" "}
                {background.attribution.photographer_url ? (
                  <a
                    href={background.attribution.photographer_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {background.attribution.photographer}
                  </a>
                ) : (
                  background.attribution.photographer
                )}{" "}
                on{" "}
                <a
                  href={
                    background.attribution.photo_url ||
                    "https://unsplash.com/?utm_source=lazystudio&utm_medium=referral"
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  Unsplash
                </a>
              </p>
            ) : null}
            </div>
          </div>
        )}
      </div>
      
      {/* Preloader Overlay */}
      {showPreloader && (
        <div className="editor-preloader-overlay">
          <div className="editor-page__canvas-loader-frame" style={canvasLoaderStyle}>
            <AiProcessingLoader />
          </div>
        </div>
      )}

      {toolPrepNode}
      <EditorWatermarkLeftPanelOverlays />
    </div>
    </WatermarkToolbarOverlayProvider>
    </ToolPrepCloseContext.Provider>
  );
};

export default Editor;

